import React, { useState, useEffect } from 'react';
import { RefreshCw, UtensilsCrossed, AlertTriangle, X } from 'lucide-react';
import FiltersSection from '../components/orders/FiltersSection';
import OrderItem from '../components/orders/OrderItem';
import RefundModal from '../components/orders/RefundModal';
import { notifyRefundRequestsChanged } from '../hooks/useRefundAlerts';
import { useOrders, HISTORY_WINDOWS } from '../hooks/useOrders';
import { isRecentlyClosed, isClosed, isUrgentNew } from '../hooks/orderSelectors';
import { PageShell, Button, cn } from '../components/ui';
import { controlClasses } from '../components/ui/Field';

const filterControl = cn(controlClasses, 'py-1.5 text-sm');

// Quantos cartões do histórico se desenham de cada vez.
//
// Não é um filtro — é um teto de desenho. O que limita o que se vê é a janela
// de tempo; isto só impede que o "Todos" tente pôr milhares de cartões no ecrã
// de uma vez e deixe o browser preso. Confundir as duas coisas foi o que fez o
// histórico mostrar oito e mais nada.
const PAGINA = 24;

const TakeawayOrders = ({ token, apiUrl, onLogout }) => {
  const {
    orders,
    loading,
    error,
    filters,
    setFilters,
    fetchOrders,
    handleStatusUpdate,
    filteredOrders,
    refundRequests,
    activeOrders,
    historyOrders,
    now,
    historyWindow,
    historyLoading,
    fetchHistory,
    cancelHistoryFetch,
  } = useOrders(token, apiUrl, onLogout);

  // O que está escolhido no seletor. Só coincide com a janela aplicada depois
  // de o pedido ter corrido — o "Todos" fica à espera de confirmação.
  const [escolha, setEscolha] = useState(historyWindow);
  const [visiveis, setVisiveis] = useState(PAGINA);
  // O pedido com o modal de reembolso aberto. Fica aqui, na página, e não no
  // cartão: um reembolso total manda o cartão para o histórico, e o modal tem de
  // continuar aberto para o staff ler a confirmação — e o código do voucher.
  const [aReembolsar, setAReembolsar] = useState(null);

  const aguardaConfirmacao = escolha === 'all' && historyWindow !== 'all';

  // Uma janela nova recomeça a contagem: continuar no 96.º cartão de uma lista
  // que passou a ser outra não faz sentido nenhum.
  useEffect(() => setVisiveis(PAGINA), [historyWindow, filters.search]);

  const escolherJanela = (key) => {
    setEscolha(key);
    // O "Todos" não dispara à primeira: pode arrastar a tabela inteira, e quem
    // clicou ao lado no seletor não contava com isso.
    if (key !== 'all') fetchHistory(key);
  };

  // Desistir é voltar ao que estava, e não só parar o pedido: sem repor o
  // seletor, ele ficava a dizer "Último ano" por cima da lista da janela
  // anterior, que é uma legenda a mentir sobre o que está no ecrã.
  const desistir = () => {
    cancelHistoryFetch();
    setEscolha(historyWindow);
  };

  const historicoVisivel = historyOrders.slice(0, visiveis);

  return (
    <PageShell
      title="Pedidos TakeAway"
      subtitle="Gestão de pedidos em tempo real"
      icon={UtensilsCrossed}
      actions={
        <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={() => fetchOrders(true)}>
          {loading ? 'A Carregar...' : 'Atualizar'}
        </Button>
      }
    >
      {error && (
        <div className="mb-6 rounded-xl bg-status-canceled-soft px-4 py-3 text-sm font-medium text-status-canceled">
          {error}
        </div>
      )}

      <FiltersSection
        filters={filters}
        onFiltersChange={setFilters}
        totalOrders={orders.length}
        filteredCount={filteredOrders.length}
      />

      {/* Primeiro de tudo, e sem limite nenhum: um pedido de reembolso pode ser
          de uma encomenda de há três dias, e não pode competir por espaço com
          as de hoje. */}
      {refundRequests.length > 0 && (
        <>
          <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-status-pending border-b border-status-pending/30 pb-2">
            <span className="h-2.5 w-2.5 rounded-full bg-status-pending animate-pulse" />
            Pedidos de Reembolso ({refundRequests.length})
          </h2>
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {refundRequests.map((order) => (
              <OrderItem
                key={order.order_id}
                order={order}
                onStatusUpdate={handleStatusUpdate}
                onRefund={setAReembolsar}
                token={token}
                apiUrl={apiUrl}
                alertaNovo={isUrgentNew(order, now)}
              />
            ))}
          </div>
        </>
      )}

      {/* Só o que ainda tem trabalho pela frente. Uma encomenda concluída sai
          daqui no momento em que fecha — continua à vista, a cores, no topo do
          histórico. Uma esquecida aberta sai uma hora depois da hora marcada,
          pelo mesmo caminho. */}
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-foreground border-b border-border pb-2">
        <span className="h-2.5 w-2.5 rounded-full bg-status-completed animate-pulse" />
        Pedidos Ativos ({activeOrders.length})
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {activeOrders.length > 0 ? (
          activeOrders.map((order) => (
            <OrderItem
              key={order.order_id}
              order={order}
              onStatusUpdate={handleStatusUpdate}
              onRefund={setAReembolsar}
              token={token}
              apiUrl={apiUrl}
              alertaNovo={isUrgentNew(order, now)}
            />
          ))
        ) : (
          <p className="col-span-full text-muted">
            {orders.length === 0 ? 'Nenhum pedido para mostrar.' : 'Nenhum pedido ativo corresponde aos filtros selecionados.'}
          </p>
        )}
      </div>

      <div className="mt-8 border-b border-border pb-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-bold text-muted">
            Histórico de Pedidos ({historyOrders.length})
          </h2>

          <div className="flex items-center gap-2">
            <label htmlFor="historyWindow" className="text-sm font-medium text-muted">
              Mostrar mais pedidos:
            </label>
            <select
              id="historyWindow"
              value={escolha}
              onChange={(e) => escolherJanela(e.target.value)}
              className={filterControl}
            >
              {HISTORY_WINDOWS.map((janela) => (
                <option key={janela.key} value={janela.key}>
                  {janela.label}
                </option>
              ))}
            </select>
          </div>

          {historyLoading && (
            <div className="flex items-center gap-2 text-sm text-muted">
              <RefreshCw className="h-4 w-4 animate-spin" />
              A carregar…
              <Button variant="ghost" size="sm" icon={X} onClick={desistir}>
                Cancelar
              </Button>
            </div>
          )}
        </div>

        {/* O "Todos" não tem teto nenhum, de propósito — foi o que se pediu.
            Mas quem carrega tem de saber o que vem aí antes de o pedido sair. */}
        {aguardaConfirmacao && !historyLoading && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-status-pending-soft px-4 py-3 text-sm">
            <AlertTriangle className="h-4 w-4 shrink-0 text-status-pending" />
            <span className="text-foreground">
              Vai carregar o histórico completo, sem limite de datas. Pode demorar e trazer
              milhares de pedidos.
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="secondary" size="sm" onClick={desistir}>
                Cancelar
              </Button>
              <Button size="sm" onClick={() => fetchHistory('all')}>
                Carregar tudo
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Sem esbatimento na grelha: é por cartão, e o que fechou na última
          hora fica a cores. É a janela em que ainda chega uma reclamação.
          As atrasadas ainda abertas também ficam a cores, com o estado à
          vista: estão aqui à espera de alguém as fechar. */}
      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {historicoVisivel.length > 0 ? (
          historicoVisivel.map((order) => (
            <OrderItem
              key={order.order_id}
              order={order}
              onStatusUpdate={handleStatusUpdate}
              onRefund={setAReembolsar}
              token={token}
              apiUrl={apiUrl}
              dimmed={isClosed(order) && !isRecentlyClosed(order, now)}
              alertaNovo={isUrgentNew(order, now)}
            />
          ))
        ) : (
          <p className="col-span-full text-muted">
            {filters.search?.trim()
              ? 'Nenhum pedido corresponde à pesquisa.'
              : 'Nenhum pedido no histórico para este período.'}
          </p>
        )}
      </div>

      {historyOrders.length > visiveis && (
        <div className="mt-6 flex justify-center">
          <Button variant="outline" onClick={() => setVisiveis((n) => n + PAGINA)}>
            Ver mais pedidos ({historyOrders.length - visiveis} por mostrar)
          </Button>
        </div>
      )}

      <RefundModal
        // Um modal novo por pedido: o valor e o método por omissão vêm do
        // pedido, e não podem ficar os do anterior.
        key={aReembolsar?.order_id}
        open={!!aReembolsar}
        order={aReembolsar}
        token={token}
        apiUrl={apiUrl}
        onClose={() => setAReembolsar(null)}
        // O servidor devolve a encomenda como ficou — com o `app_status` novo
        // quando o reembolso foi total, e com a hora da mudança. É o que faz o
        // cartão saltar já para a secção certa, em vez de esperar pelo próximo
        // refresh. O remendo local só entra se a resposta vier sem ela.
        onDone={(resultado) => {
          handleStatusUpdate(
            resultado?.order || { ...aReembolsar, refund_request_status: 'resolved' }
          );
          notifyRefundRequestsChanged();
        }}
      />
    </PageShell>
  );
};

export default TakeawayOrders;
